import { getVerticalDistance, getDistance } from 'src/utils';
import {
  feetToMile,
  feetToNauticalMile,
  TDistanceUnit,
  TVerticalDistanceUnit,
  feetToKilometerConverter,
} from '@ems/client-design-system';
import { OperationSummary } from 'src/app/gql/__generated__/hooks';
import { GateDeviationDataPoint } from './types';
import { UNIT_FOOT, UNIT_KILOMETER, UNIT_MILE, UNIT_NAUTICAL_MILE } from 'src/constants';

// Chart configuration constants
const CHART_CONFIG = {
  DECIMAL_UNITS: {
    STEP_SIZE: 0.25,
    PADDING: 0.5,
  },
  IMPERIAL_UNITS: {
    STEP_SIZE: 200,
    PADDING: 240,
  },
  METRIC_UNITS: {
    STEP_SIZE: 500,
    PADDING: 600,
  },
  ALTITUDE: {
    STEP_SIZE_FEET: 2000,
    STEP_SIZE_METERS: 500,
  },
  EXPORT: {
    BACKGROUND_COLOR: '#ffffff',
    SCALE: 2,
    FORMAT: 'image/jpeg',
    QUALITY: 1.0,
    FILE_PREFIX: 'gate-deviation-chart',
  },
  BUFFER_PERCENTAGE: 1.2,
  PRECISION: 100, // For rounding to 2 decimal places
} as const;

/**
 * Converts deviation and altitude values from ft to user's preferred units
 */
export const convertGateIntersectionData = (
  operation: OperationSummary,
  conversionSettings: {
    convertTo: TDistanceUnit;
    convertFrom: TDistanceUnit;
    returnValueType: 'string' | 'number';
  },
  conversionSettingsVertical: {
    convertTo: TVerticalDistanceUnit;
    convertFrom: TVerticalDistanceUnit;
    returnValueType: 'string' | 'number';
  }
): GateDeviationDataPoint | null => {
  const gateIntersection = operation.gateIntersections?.[0];

  if (!gateIntersection) {
    return null;
  }

  const deviationX = (() => {
    const rawDeviation = gateIntersection.deviationX || 0;

    if (
      conversionSettings.convertFrom === UNIT_FOOT &&
      conversionSettings.convertTo === UNIT_MILE
    ) {
      return feetToMile(rawDeviation);
    }

    if (
      conversionSettings.convertFrom === UNIT_FOOT &&
      conversionSettings.convertTo === UNIT_NAUTICAL_MILE
    ) {
      return feetToNauticalMile(rawDeviation);
    }

    if (
      conversionSettings.convertFrom === UNIT_FOOT &&
      conversionSettings.convertTo === UNIT_KILOMETER
    ) {
      return feetToKilometerConverter(rawDeviation);
    }

    return getDistance(rawDeviation, conversionSettings) as number;
  })();

  const deviationY = getVerticalDistance(
    // gateIntersection.deviationY || 0,
    gateIntersection.position.altitude || 0,
    conversionSettingsVertical
  ) as number;

  // Get altitude from position with safe fallback
  const altitude = gateIntersection.position?.altitude ?? 0;
  const convertedAltitude = getVerticalDistance(altitude, conversionSettingsVertical) as number;

  return {
    x: deviationX,
    y: deviationY,
    direction: (gateIntersection as any).direction ?? 'Unknown',
    time: (gateIntersection as any).time ?? new Date().toISOString(),
    altitude: convertedAltitude,
  };
};

/**
 * Gets the color for an operation type from the theme color list
 */
export const getOperationColor = (
  operationType: string | undefined,
  colorList: Record<string, string>
): string => {
  const opType = operationType?.toLowerCase() || 'arrival';
  return colorList[opType] || colorList.arrival;
};

/**
 * Determines if the gate intersection direction is opposite
 */
export const isOppositeDirection = (operation: OperationSummary): boolean => {
  const gateIntersection = operation.gateIntersections?.[0];
  return (gateIntersection as any)?.direction === 'Opposite';
};

/**
 * Formats time for tooltip display (time only, no date)
 */
export const formatTooltipTime = (timeString: string): string =>
  new Date(timeString).toLocaleTimeString();

/**
 * Configuration for axis ranges and step sizes
 */
export interface AxisConfig {
  min: number;
  max: number;
  stepSize: number;
  ticks?: number[]; // Explicit tick values for precise control
}

/**
 * Calculates the Y-axis configuration based on actual data points with a buffer
 *
 * @param operations - Array of operations with gate intersection data
 * @param isFeet - Whether the unit is feet (true) or meters (false)
 * @param conversionSettings - Settings for unit conversion
 * @returns Y-axis configuration with min, max, and step size
 */
export const calculateYAxisConfig = (
  operations: OperationSummary[],
  isFeet: boolean,
  conversionSettings: {
    convertTo: TVerticalDistanceUnit;
    convertFrom: TVerticalDistanceUnit;
    returnValueType: 'string' | 'number';
  }
): AxisConfig => {
  // Extract all Y values (altitudes) from data points
  const allYValues = operations
    .map(op => {
      const dataPoint = op.gateIntersections?.[0];
      if (!dataPoint?.position?.altitude) {
        return 0;
      }
      return getVerticalDistance(dataPoint.position.altitude, conversionSettings) as number;
    })
    .filter(val => !isNaN(val));

  const stepSize = isFeet
    ? CHART_CONFIG.ALTITUDE.STEP_SIZE_FEET
    : CHART_CONFIG.ALTITUDE.STEP_SIZE_METERS;

  // Calculate max with buffer and round up to next step size multiple
  const maxY = Math.max(...allYValues, 0);
  const maxWithBuffer = maxY * CHART_CONFIG.BUFFER_PERCENTAGE;
  const max = Math.ceil(maxWithBuffer / stepSize) * stepSize;

  return { min: 0, max, stepSize };
};

/**
 * Calculates the X-axis configuration based on actual data points with a buffer
 *
 * @param operations - Array of operations with gate intersection data
 * @param isFeet - Whether the unit is feet (true) or meters (false)
 * @param conversionSettings - Settings for unit conversion
 * @returns X-axis configuration with min, max, and step size (centered at 0)
 */
export const calculateXAxisConfig = (
  operations: OperationSummary[],
  isFeet: boolean,
  conversionSettings: {
    convertTo: TDistanceUnit;
    convertFrom: TDistanceUnit;
    returnValueType: 'string' | 'number';
  }
): AxisConfig => {
  // Extract all X values (lateral deviations) from data points
  const allXValues = operations
    .map(op => {
      const deviationX = op.gateIntersections?.[0]?.deviationX || 0;

      if (
        conversionSettings.convertFrom === UNIT_FOOT &&
        conversionSettings.convertTo === UNIT_MILE
      ) {
        return feetToMile(deviationX);
      } else if (
        conversionSettings.convertFrom === UNIT_FOOT &&
        conversionSettings.convertTo === UNIT_NAUTICAL_MILE
      ) {
        return feetToNauticalMile(deviationX);
      } else if (
        conversionSettings.convertFrom === UNIT_FOOT &&
        conversionSettings.convertTo === UNIT_KILOMETER
      ) {
        return feetToKilometerConverter(deviationX);
      }

      return getDistance(deviationX, conversionSettings) as number;
    })
    .filter(val => !isNaN(val));

  // Determine step size and padding based on unit
  const isMiles = conversionSettings.convertTo === UNIT_MILE;
  const isNauticalMiles = conversionSettings.convertTo === UNIT_NAUTICAL_MILE;
  const isKilometers = conversionSettings.convertTo === UNIT_KILOMETER;
  const isDecimalUnit = isMiles || isNauticalMiles || isKilometers;

  const stepSize = isDecimalUnit
    ? CHART_CONFIG.DECIMAL_UNITS.STEP_SIZE
    : isFeet
    ? CHART_CONFIG.IMPERIAL_UNITS.STEP_SIZE
    : CHART_CONFIG.METRIC_UNITS.STEP_SIZE;

  const padding = isDecimalUnit
    ? CHART_CONFIG.DECIMAL_UNITS.PADDING
    : isFeet
    ? CHART_CONFIG.IMPERIAL_UNITS.PADDING
    : CHART_CONFIG.METRIC_UNITS.PADDING;

  // Calculate max absolute deviation
  const maxAbsX = Math.max(...allXValues.map(Math.abs), stepSize);

  // For decimal units, generate explicit ticks to avoid Chart.js rounding issues
  if (isDecimalUnit) {
    const maxWithBuffer = maxAbsX + padding;
    // Round up to nearest step size multiple to ensure perfect tick alignment
    const max = Math.ceil(maxWithBuffer / stepSize) * stepSize;

    // Generate explicit tick values
    const ticks: number[] = [];
    for (let tick = -max; tick <= max; tick += stepSize) {
      // Round to avoid floating point precision issues
      ticks.push(Math.round(tick * CHART_CONFIG.PRECISION) / CHART_CONFIG.PRECISION);
    }

    return { min: -max, max, stepSize, ticks };
  }

  // For other units, use percentage buffer
  const maxWithBuffer = maxAbsX * CHART_CONFIG.BUFFER_PERCENTAGE;
  const max = Math.ceil(maxWithBuffer / stepSize) * stepSize;

  // X-axis is centered at 0, so min is negative of max
  return { min: -max, max, stepSize };
};

/**
 * Exports a chart container as a JPG image with white background
 *
 * @param containerElement - The HTML element containing the chart
 * @returns Promise that resolves when export is complete
 */
export const exportChartAsImage = async (containerElement: HTMLElement): Promise<void> => {
  const html2canvas = (await import('html2canvas')).default;

  const canvas = await html2canvas(containerElement, {
    backgroundColor: CHART_CONFIG.EXPORT.BACKGROUND_COLOR,
    scale: CHART_CONFIG.EXPORT.SCALE,
  });

  // Convert to JPG and trigger download
  const url = canvas.toDataURL(CHART_CONFIG.EXPORT.FORMAT, CHART_CONFIG.EXPORT.QUALITY);
  const link = document.createElement('a');
  const dateString = new Date().toISOString().split('T')[0];
  link.download = `${CHART_CONFIG.EXPORT.FILE_PREFIX}-${dateString}.jpg`;
  link.href = url;
  link.click();
};
